import {
  useAiChatAttached,
  useAiChatMessage,
} from '@euler/app/flows/ai-suite/chat/AiChatActions';
import {
  CardLoading,
  CardNotice,
  ChatCard,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { BiButton } from '@euler/app/flows/bi-dashboards/components/ui/BiButton';
import { FileSpreadsheet, Trash2 } from 'lucide-react-native';
import { Fragment, memo, useState } from 'react';
import { View } from 'react-native';
import { BiProposalAddButton } from '../bi-insight-proposals/BiProposalAddButton';
import { useBiProposalSelection } from '../bi-insight-proposals/functions/useBiProposalSelection';
import { DuplicateOffer } from './DuplicateOffer';
import { UploadSummary } from './UploadSummary';
import {
  BiUploadPhase,
  biUploadExpiredText,
  biUploadFailureText,
  biUploadPhase,
  isBiUploadDeletable,
} from './functions/biUploadPhase';
import { biUploadSubtitle } from './functions/biUploadSubtitle';
import { useBiUpload } from './functions/useBiUpload';
import { useBiUploadDelete } from './functions/useBiUploadDelete';
import { BlockedSheets } from './sheet-picker/BlockedSheets';
import { SheetPicker } from './sheet-picker/SheetPicker';
import { SheetSection } from './sheet-section/SheetSection';

/**
 * One file upload. The newest card of the upload in the conversation
 * follows it and takes its taps; an older one sums it up in a line.
 */
export const UploadCard = memo(({ uploadId }: { uploadId: string }) => {
  const attached = useAiChatAttached();
  const { biUploads } = useAiChatMessage();
  const follow = attached && biUploads.includes(uploadId);
  const state = useBiUpload(uploadId, follow);
  const [importAsNew, setImportAsNew] = useState(false);
  const selection = useBiProposalSelection();
  const [deleted, setDeleted] = useState(false);
  const removal = useBiUploadDelete(() => setDeleted(true));
  if (deleted)
    return (
      <ChatCard icon={FileSpreadsheet} color={aiColors.amber} title="表格">
        <CardNotice text="这次上传已删除。" />
      </ChatCard>
    );
  if (state.kind !== 'ready')
    return (
      <ChatCard icon={FileSpreadsheet} color={aiColors.amber} title="表格">
        {state.kind === 'loading' ? (
          <CardLoading label="正在读取表格…" />
        ) : state.kind === 'missing' ? (
          <CardNotice text="找不到这个表格，可能已被删除。" />
        ) : (
          <CardNotice tone="danger" text="表格没有加载出来，请稍后重试。" />
        )}
      </ChatCard>
    );
  const { view } = state;
  const phase: BiUploadPhase = biUploadPhase(view, importAsNew);
  const proposals = view.sheets.flatMap(sheet =>
    sheet.upload ? sheet.upload.proposalDraftIds : [],
  );
  const lastWithProposals = view.sheets.reduce(
    (last, sheet, index) =>
      sheet.upload?.proposalDraftIds.length ? index : last,
    -1,
  );
  return (
    <ChatCard
      icon={FileSpreadsheet}
      color={aiColors.amber}
      title={view.fileName || '表格'}
      subtitle={biUploadSubtitle(view)}
    >
      {!follow ? (
        <UploadSummary view={view} />
      ) : phase.kind === 'reading' ? (
        <CardLoading label="正在读取表格…" />
      ) : phase.kind === 'failed' ? (
        <>
          <CardNotice tone="danger" text={biUploadFailureText(phase.failure)} />
          <BlockedSheets view={view} />
        </>
      ) : phase.kind === 'expired' ? (
        <CardNotice text={biUploadExpiredText(view)} />
      ) : phase.kind === 'deleted' ? (
        <CardNotice text="这个表格已删除。" />
      ) : phase.kind === 'duplicate' ? (
        <DuplicateOffer
          view={view}
          onImportAsNew={() => setImportAsNew(true)}
        />
      ) : phase.kind === 'sheets' ? (
        <SheetPicker view={view} />
      ) : (
        <>
          {view.sheets.map((sheet, index) =>
            sheet.upload ? (
              <Fragment key={sheet.upload.id}>
                <SheetSection
                  fileName={view.fileName}
                  sheet={sheet}
                  upload={sheet.upload}
                  selection={selection}
                />
                {/* One add for every sheet's proposals, under the last. */}
                {index === lastWithProposals ? (
                  <BiProposalAddButton
                    draftIds={proposals}
                    selection={selection}
                  />
                ) : null}
              </Fragment>
            ) : null,
          )}
        </>
      )}
      {follow && phase.kind !== 'reading' && isBiUploadDeletable(view) ? (
        <View style={{ alignItems: 'flex-start' }}>
          {removal.error ? (
            <CardNotice tone="danger" text={removal.error} />
          ) : null}
          <BiButton
            icon={Trash2}
            title="删除这次上传"
            tone="quiet"
            loading={removal.deleting}
            onPress={() => void removal.remove(view)}
          />
        </View>
      ) : null}
    </ChatCard>
  );
});
